import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { BsCloudUpload } from "react-icons/bs";
import useUploadBulkImportFile from "api/BulkBookImport/useUploadBulkImportFile";
import useBulkImportLimits, {
	DEFAULT_BULK_IMPORT_LIMITS,
} from "api/BulkBookImport/useBulkImportLimits";
import StepPanel from "../StepPanel";
import StatusBanner from "../StatusBanner";
import FileChip from "../FileChip";

const NEED_BOOK_LIST =
	"Upload your filled-in book list before you can continue.";

const UploadBookListStep = ({
	importId,
	manifestUpload,
	onUploaded,
	onBack,
	onContinue,
}) => {
	const [fileName, setFileName] = useState(
		manifestUpload?.original_name || ""
	);
	const [fileSize, setFileSize] = useState(
		manifestUpload?.size_bytes ?? null
	);
	const [localReady, setLocalReady] = useState(Boolean(manifestUpload));
	const [validationError, setValidationError] = useState(null);
	const [nudge, setNudge] = useState(false);
	const dropzoneRef = useRef(null);
	const upload = useUploadBulkImportFile();
	const { data: limits = DEFAULT_BULK_IMPORT_LIMITS } = useBulkImportLimits();
	const maxManifestBytes = (limits.max_manifest_mb || 5) * 1024 * 1024;
	const mimeLabel = useMemo(
		() =>
			(limits.manifest_mimes || [])
				.map((m) => `.${m}`)
				.join(", ") || ".xlsx, .xls, .csv",
		[limits.manifest_mimes]
	);

	useEffect(() => {
		if (manifestUpload) {
			setLocalReady(true);
			setNudge(false);
			setFileName(manifestUpload.original_name || fileName);
			if (manifestUpload.size_bytes != null) {
				setFileSize(manifestUpload.size_bytes);
			}
		}
	}, [manifestUpload]); // eslint-disable-line react-hooks/exhaustive-deps

	const hasBookList = localReady || Boolean(manifestUpload);

	const handleFile = useCallback(
		async (file) => {
			if (!file || !importId) return;
			if (file.size > maxManifestBytes) {
				setValidationError(
					`Book list must be ${limits.max_manifest_mb} MB or smaller.`
				);
				return;
			}
			setValidationError(null);
			setNudge(false);
			setFileName(file.name);
			setFileSize(file.size);
			try {
				await upload.mutateAsync({
					importId,
					file,
					type: "manifest",
				});
				setLocalReady(true);
				await onUploaded?.();
			} catch {
				setLocalReady(Boolean(manifestUpload));
			}
		},
		[
			importId,
			upload,
			onUploaded,
			manifestUpload,
			maxManifestBytes,
			limits.max_manifest_mb,
		]
	);

	const onDrop = useCallback(
		(accepted) => {
			if (accepted?.[0]) handleFile(accepted[0]);
		},
		[handleFile]
	);

	const onDropRejected = useCallback(
		(rejections) => {
			const tooBig = rejections.some((r) =>
				r.errors?.some((e) => e.code === "file-too-large")
			);
			const badType = rejections.some((r) =>
				r.errors?.some((e) => e.code === "file-invalid-type")
			);
			if (tooBig) {
				setValidationError(
					`Book list must be ${limits.max_manifest_mb} MB or smaller.`
				);
			} else if (badType) {
				setValidationError(`Use an Excel or CSV file (${mimeLabel}).`);
			} else {
				setValidationError("That file could not be added.");
			}
		},
		[limits.max_manifest_mb, mimeLabel]
	);

	const handleBlockedContinue = useCallback(() => {
		setNudge(true);
		toast.error(NEED_BOOK_LIST);
		dropzoneRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "center",
		});
	}, []);

	const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
		onDrop,
		onDropRejected,
		multiple: false,
		noClick: true,
		maxSize: maxManifestBytes,
		accept: {
			"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
				".xlsx",
			],
			"application/vnd.ms-excel": [".xls"],
			"text/csv": [".csv"],
		},
		disabled: upload.isLoading || !importId,
	});

	const highlightMissing = nudge && !hasBookList;

	return (
		<StepPanel
			stepNumber={2}
			title="Upload your filled-in book list"
			description="This is the same Excel file you downloaded in step 1, with the file name columns filled in."
			status={
				hasBookList ? (
					<StatusBanner tone="success" title="Book list ready" />
				) : highlightMissing ? (
					<StatusBanner
						tone="warning"
						title="Book list still needed"
						action={
							<button
								type="button"
								className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-50"
								onClick={open}
							>
								Choose file
							</button>
						}
					>
						Attach the Excel or CSV you filled in, then try Continue
						again.
					</StatusBanner>
				) : null
			}
			nav={{
				onBack,
				onNext: onContinue,
				onNextBlocked: handleBlockedContinue,
				backLabel: "← Back",
				nextLabel: "Continue to upload files →",
				nextDisabled: !hasBookList,
				nextDisabledReason: hasBookList ? null : NEED_BOOK_LIST,
				busy: upload.isLoading,
				busyLabel: "Uploading…",
			}}
		>
			<div className="space-y-3">
				<div
					ref={dropzoneRef}
					{...getRootProps()}
					className={`rounded-xl border-2 border-dashed px-4 py-5 text-center transition-all duration-200 ${
						isDragActive
							? "portal-upload-active border-akgreen bg-akgreen bg-opacity-10"
							: highlightMissing || validationError
							? "border-amber-400 bg-amber-50 bg-opacity-50 ring-2 ring-amber-200"
							: "border-gray-200 bg-gray-50 bg-opacity-80 hover:border-akgreen border-opacity-50"
					}`}
				>
					<input {...getInputProps()} />
					<div className="mx-auto mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-white text-akgreen shadow-sm">
						<BsCloudUpload className="h-4 w-4" aria-hidden />
					</div>
					<p className="text-sm font-medium text-gray-900">
						{isDragActive
							? "Drop your book list here…"
							: highlightMissing
							? "Add your book list here to continue"
							: "Drag & drop your book list, or browse"}
					</p>
					<p className="mt-0.5 text-xs text-gray-500">
						Accepted: {mimeLabel} · max {limits.max_manifest_mb} MB · up
						to {limits.max_items} rows
					</p>
					<button
						type="button"
						className={`mt-3 rounded-lg border px-3 py-1.5 text-sm font-medium transition disabled:opacity-50 ${
							highlightMissing
								? "border-amber-400 bg-amber-50 text-amber-950 hover:bg-amber-100"
								: "border-akgreen border-opacity-40 bg-white text-akgreenest hover:bg-akgreen bg-opacity-5"
						}`}
						disabled={upload.isLoading || !importId}
						onClick={open}
					>
						{upload.isLoading
							? "Uploading…"
							: hasBookList
							? "Replace book list"
							: "Choose file"}
					</button>
				</div>

				{validationError ? (
					<div role="alert" aria-live="polite">
						<StatusBanner
							tone="warning"
							title="Book list could not be added"
							action={
								<button
									type="button"
									className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-50"
									onClick={() => setValidationError(null)}
								>
									Dismiss
								</button>
							}
						>
							{validationError}
						</StatusBanner>
					</div>
				) : null}

				{(fileName || manifestUpload?.original_name) && (
					<FileChip
						name={fileName || manifestUpload?.original_name}
						type="manifest"
						status={hasBookList ? "ready" : "waiting"}
						sizeBytes={fileSize ?? manifestUpload?.size_bytes}
					/>
				)}
			</div>
		</StepPanel>
	);
};

export default UploadBookListStep;
